/**
 * Pure helpers for the review workflow on the Timesheet Exceptions page.
 *
 * Split out of TimesheetExceptionsPage.tsx so the derived logic can be tested
 * directly — the same arrangement as miscTaskTrendHelpers.ts. Nothing here
 * touches React or the DOM.
 *
 * Reviews are entirely optional: the page opens on All (today's view), and the
 * review filter is just another way to slice it.
 */
import {
  ExceptionEvent, ExceptionCounts, ExceptionReviewAction, ExceptionReviewResult,
  TimesheetExceptionsPayload,
} from '../../interfaces/TimesheetExceptions';

export type ReviewFilter = 'all' | 'unreviewed' | 'reviewed';

/**
 * True when the API sent review state at all. An API that predates the feature
 * leaves `reviewed` undefined on every event; the page then hides every review
 * control and renders exactly as before.
 */
export function reviewSupported(events: ExceptionEvent[]): boolean {
  return events.some((e) => e.reviewed !== undefined);
}

/**
 * Mirror of the API's rule (controllers/admin/timesheetExceptions.ts) so a PATCH
 * result — which carries no countsAsUnreviewed — can be applied in place. Replaced
 * submissions (Superseded, or Withdrawn with a later submission) never count.
 */
export function countsAsUnreviewed(
  e: Pick<ExceptionEvent, 'reviewed' | 'submissionState' | 'supersededByEventID'>,
): boolean {
  if (e.reviewed) return false;
  return e.submissionState === 'Current' || (e.submissionState === 'Withdrawn' && e.supersededByEventID == null);
}

/** Whether one event belongs under the review segment. */
export function matchesReviewFilter(e: ExceptionEvent, filter: ReviewFilter): boolean {
  if (filter === 'unreviewed') return e.countsAsUnreviewed ?? countsAsUnreviewed(e);
  if (filter === 'reviewed') return !!e.reviewed;
  return true;
}

/**
 * Events in scope for the review segment and the "Hide replaced" toggle — the
 * base set before the kind/search filters narrow it. "Hide replaced" is a no-op
 * under Unreviewed (replaced submissions are already left out), so it's ignored.
 */
export function eventsInReviewScope(
  events: ExceptionEvent[], filter: ReviewFilter, hideReplaced: boolean,
): ExceptionEvent[] {
  const applyHide = hideReplaced && filter !== 'unreviewed';
  return events
    .filter((e) => (applyHide ? e.submissionState !== 'Superseded' : true))
    .filter((e) => matchesReviewFilter(e, filter));
}

/** The review counts the segment badges read, recomputed from the events. */
export function recountReview(counts: ExceptionCounts, events: ExceptionEvent[]): ExceptionCounts {
  return {
    ...counts,
    unreviewed: events.filter((e) => e.countsAsUnreviewed ?? countsAsUnreviewed(e)).length,
    reviewed: events.filter((e) => !!e.reviewed).length,
  };
}

/**
 * Apply a PATCH result in place: the event's review fields and history, its
 * countsAsUnreviewed, and the payload's review counts. Every other field is left
 * alone, and an event id that isn't present returns the payload unchanged.
 */
export function applyReviewResult(
  payload: TimesheetExceptionsPayload, result: ExceptionReviewResult,
): TimesheetExceptionsPayload {
  if (!payload.events.some((e) => e.timesheetExceptionEventID === result.timesheetExceptionEventID)) {
    return payload;
  }
  const events = payload.events.map((e) => {
    if (e.timesheetExceptionEventID !== result.timesheetExceptionEventID) return e;
    const next: ExceptionEvent = {
      ...e,
      reviewed: result.reviewed,
      reviewedByAdminEmployeeID: result.reviewedByAdminEmployeeID,
      reviewedByName: result.reviewedByName,
      reviewedAt: result.reviewedAt,
      reviewNote: result.reviewNote,
      reviewHistory: result.reviewHistory,
    };
    next.countsAsUnreviewed = countsAsUnreviewed(next);
    return next;
  });
  // Another card's "Previous submission reviewed by…" line reads the replaced
  // submission's state; keep it in step with what was just changed.
  // The API only names the previous submission when it was reviewed, so an unset
  // link is found by seq instead.
  const source = payload.events.find((x) => x.timesheetExceptionEventID === result.timesheetExceptionEventID)!;
  const withPrev = events.map((e) => {
    if (e.timesheetExceptionEventID === result.timesheetExceptionEventID) return e;
    const prev = e.previousSubmissionReview;
    const linksHere = prev
      ? prev.timesheetExceptionEventID === result.timesheetExceptionEventID
      : isNextSubmission(payload.events, source, e);
    if (!linksHere) return e;
    return {
      ...e,
      previousSubmissionReview: result.reviewed
        ? {
          timesheetExceptionEventID: result.timesheetExceptionEventID,
          submissionSeq: source.submissionSeq,
          reviewedByAdminEmployeeID: result.reviewedByAdminEmployeeID,
          reviewedByName: result.reviewedByName,
          reviewedAt: result.reviewedAt,
          reviewNote: result.reviewNote,
        }
        : null,
    };
  });
  return { ...payload, events: withPrev, counts: recountReview(payload.counts, withPrev) };
}

/**
 * True when `candidate` is the submission immediately after `eventID` for the same
 * employee and week — the API's "previous submission" lookup run in reverse
 * (highest SubmissionSeq below the candidate's). The list is one pay period, so
 * same employee = same week.
 */
function isNextSubmission(events: ExceptionEvent[], source: ExceptionEvent, candidate: ExceptionEvent): boolean {
  if (source.employeeID !== candidate.employeeID) return false;
  if (source.submissionSeq >= candidate.submissionSeq) return false;
  const prior = events
    .filter((x) => x.employeeID === candidate.employeeID && x.submissionSeq < candidate.submissionSeq)
    .reduce<ExceptionEvent | null>((best, x) => (!best || x.submissionSeq > best.submissionSeq ? x : best), null);
  return prior?.timesheetExceptionEventID === source.timesheetExceptionEventID;
}

/**
 * Which "no rows" message to show once events exist but none are visible, in
 * precedence order: the review segment's own message first (so an empty Reviewed
 * list says so instead of blaming the search box), then the generic filter one.
 * Null when something is visible or the week has no events (the page's own
 * empty states cover that).
 */
export type FilterEmptyState = 'unreviewed' | 'reviewed' | 'filter' | null;

export function filterEmptyState(
  totalEvents: number, visibleEvents: number, filter: ReviewFilter, reviewScopeCount: number,
): FilterEmptyState {
  if (totalEvents === 0 || visibleEvents > 0) return null;
  // Only claim "none in this segment" when the segment itself is empty — if it has
  // rows that the search/kind filters removed, that's the generic message.
  if (filter === 'unreviewed' && reviewScopeCount === 0) return 'unreviewed';
  if (filter === 'reviewed' && reviewScopeCount === 0) return 'reviewed';
  return 'filter';
}

const HISTORY_LABELS: Record<ExceptionReviewAction, string> = {
  Reviewed: 'Reviewed',
  NoteEdited: 'Note edited',
  Cleared: 'Cleared',
};

export function historyActionLabel(action: ExceptionReviewAction | string): string {
  return HISTORY_LABELS[action as ExceptionReviewAction] ?? action;
}

const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * A review timestamp (server UTC ISO) in the viewer's local time — the same
 * format GpsExceptionsPage uses, e.g. "Oct 4, 2026 2:05p".
 */
export function formatReviewedAt(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  let h = d.getHours();
  const m = d.getMinutes();
  const ap = h >= 12 ? 'p' : 'a';
  h = h % 12 || 12;
  return `${MON[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()} ${h}:${m.toString().padStart(2, '0')}${ap}`;
}

/** Hover title for the card's review pill: reviewer · time — "note". */
export function reviewTooltip(
  e: Pick<ExceptionEvent, 'reviewed' | 'reviewedByName' | 'reviewedAt' | 'reviewNote'>,
): string | undefined {
  if (!e.reviewed) return undefined;
  const at = e.reviewedAt ? ` · ${formatReviewedAt(e.reviewedAt)}` : '';
  const note = e.reviewNote ? ` — "${e.reviewNote}"` : '';
  return `Reviewed by ${e.reviewedByName ?? 'Unknown'}${at}${note}`;
}
